import { useEffect, useRef, useState } from 'react'

import { ApiError } from '@/lib/api'
import { Button, ErrorBanner, Field, Input, Modal, Textarea, useSprintMutations } from '@/features/issue'

/**
 * Create a sprint from the planning screen.
 *
 * Dates go over the wire as bare `yyyy-mm-dd`, which is exactly what an
 * `<input type="date">` produces — the API stores them and hands them back as
 * midnight-UTC timestamps, so nothing here reformats a value on its way out.
 */
export function CreateSprintModal({
  open,
  onClose,
  slug,
  projectKey,
}: {
  open: boolean
  onClose: () => void
  slug: string | null
  projectKey: string | null
}) {
  const [name, setName] = useState('')
  const [goal, setGoal] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const { create } = useSprintMutations(slug, projectKey)
  // See CreateIssueModal: the mutation object is new on every render.
  const resetMutation = useRef(create.reset)
  resetMutation.current = create.reset

  useEffect(() => {
    if (!open) return
    setName('')
    setGoal('')
    setStartDate('')
    setEndDate('')
    resetMutation.current()
  }, [open])

  const fieldError = (field: string) =>
    create.error instanceof ApiError ? create.error.fieldError(field) : undefined

  const submit = () => {
    if (!name.trim()) return
    create.mutate(
      {
        name: name.trim(),
        goal: goal.trim() || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New sprint"
      description="It starts in planning — nothing moves until you start it."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!name.trim() || create.isPending || !projectKey}
          >
            {create.isPending ? 'Creating…' : 'Create sprint'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        {create.error ? <ErrorBanner error={create.error} /> : null}

        <Field label="Name" htmlFor="sprint-name" error={fieldError('name')}>
          <Input
            id="sprint-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Sprint 4"
            maxLength={255}
            autoComplete="off"
          />
        </Field>

        <Field label="Goal" htmlFor="sprint-goal">
          <Textarea
            id="sprint-goal"
            rows={2}
            value={goal}
            onChange={(event) => setGoal(event.target.value)}
            placeholder="What is this sprint for?"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Starts" htmlFor="sprint-start" error={fieldError('start_date')}>
            <Input
              id="sprint-start"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </Field>
          <Field label="Ends" htmlFor="sprint-end" error={fieldError('end_date')}>
            <Input
              id="sprint-end"
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </Field>
        </div>

        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  )
}
